import { ChangeDetectionStrategy, Component, OnInit, computed, inject } from '@angular/core';
import {
  InfiniteScrollCustomEvent,
  IonButton,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EmptyResultComponent,
  InfiniteScrollHeaderComponent,
  SkeletonItemTwoColumnsComponent,
  SpinnerLoadingComponent,
  type IInfiniteScrollHeaderConfig,
} from '@okcargo-apps/layout-ionic';
import { MessageUiIonicComponent, MessagesUiService } from '@okcargo-apps/messages-ui';
import { UserItemComponent } from '../user-item/user-item.component';
import { USERS_LIST_MESSAGE_OBJECT_ID } from '../users-store.model';
import { UsersStoreService } from '../users-store.service';

@Component({
  selector: 'lib-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonButton,
    IonList,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    InfiniteScrollHeaderComponent,
    SpinnerLoadingComponent,
    MessageUiIonicComponent,
    TranslateModule,
    UserItemComponent,
    SkeletonItemTwoColumnsComponent,
    EmptyResultComponent,
  ],
})
export class UserListComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly usersStoreService = inject(UsersStoreService);

  public readonly usersVM = this.usersStoreService.usersVM;

  // The users are not refreshed by the general application refresh: each component that consumes
  // them reloads the data when it is initialized (see UsersStoreService).
  public ngOnInit(): void {
    this.usersStoreService.reloadUsersOnComponentInit();
  }

  public readonly messages = computed(() =>
    this.messagesUiService.getMessagesForObjectId(USERS_LIST_MESSAGE_OBJECT_ID),
  );

  protected readonly headerConfig = computed<IInfiniteScrollHeaderConfig>(() => {
    const vm = this.usersVM();

    return {
      text: this.translate.instant('general.item_count', { count: vm.totalElements }),
      lastUpdated: vm.lastLoadedAt,
      staleMessage: vm.isStale ? this.translate.instant('general.stale_data') : undefined,
      refreshButton: vm.isStale
        ? {
            label: this.translate.instant('general.refresh'),
            action: () => this.usersStoreService.reloadUsers('user'),
          }
        : undefined,
    };
  });

  protected onIonInfinite(event: InfiniteScrollCustomEvent): void {
    this.usersStoreService.loadNextUsersPage();
    event.target.complete();
  }

  protected onRetry(): void {
    this.usersStoreService.retryUsers();
  }
}
